import React, { useCallback } from "react";
import Select from "react-select";
import AsyncSelect from "react-select/async";

import { CLIENT_TYPES } from "utils";
import { request } from "hooks/useOkoApi/useOkoApi";

/**
 * The five educator_type enum values (src/common/types.py EducatorType),
 * as value/label pairs. Kept here so the picker is self-contained.
 */
export const EDUCATOR_TYPES = [
  { value: "teacher", label: "Teacher" },
  { value: "math_specialist", label: "Math Specialist" },
  { value: "school_administrator", label: "School Administrator" },
  { value: "district_administrator", label: "District Administrator" },
  { value: "other", label: "Other" },
];

export const userLabel = (u) => {
  const name = [u.first_name, u.last_name].filter(Boolean).join(" ");
  const who = name || u.display_name || "(no name)";
  return u.email ? `${who} — ${u.email}` : who;
};

export const clientLabel = (c) => (c.state ? `${c.name} (${c.state})` : c.name);

/**
 * Human label for a target's subject_value, given a resolved {id -> label} map.
 * For user/school/client this is the entity's name/email (falling back to the
 * raw id when unresolved — e.g. a deleted row). For client_type/educator_type
 * the value is already human-readable, so we show its enum label.
 */
export const subjectDisplay = (subjectType, subjectValue, labelsById = {}) => {
  if (subjectType === "client_type") {
    const opt = CLIENT_TYPES.find((o) => o.value === subjectValue);
    return opt ? opt.label : subjectValue;
  }
  if (subjectType === "educator_type") {
    const opt = EDUCATOR_TYPES.find((o) => o.value === subjectValue);
    return opt ? opt.label : subjectValue;
  }
  return labelsById[subjectValue] || subjectValue;
};

/**
 * Type-aware picker for a feature-flag target's subject_value.
 *
 * - user / school / client → a remote search dropdown (AsyncSelect) that shows
 *   real names/emails and yields the entity's UUID as the value.
 * - client_type / educator_type → a fixed dropdown of the known enum values.
 *
 * Always calls onChange(subjectValueString) with the string the backend
 * stores (a UUID for user/school/client, the enum value otherwise), or "".
 * `value` is the current subject_value string (used to show the raw value when
 * we only have the id, e.g. after re-open).
 */
export const SubjectValuePicker = ({
  subjectType,
  value,
  valueLabel,
  onChange,
}) => {
  const searchUsers = useCallback(async (input) => {
    if (!input || input.length < 2) return [];
    try {
      const data = await request(
        `admin/users?search=${encodeURIComponent(input)}&limit=20`,
        { method: "GET" }
      );
      return (data?.users || []).map((u) => ({
        value: u.id,
        label: userLabel(u),
      }));
    } catch (e) {
      return [];
    }
  }, []);

  const searchClients = useCallback(async (input) => {
    if (!input || input.length < 2) return [];
    try {
      const data = await request(
        `admin/clients/search?q=${encodeURIComponent(input)}&limit=20`,
        { method: "GET" }
      );
      return (data?.results || []).map((c) => ({
        value: c.id,
        label: clientLabel(c),
      }));
    } catch (e) {
      return [];
    }
  }, []);

  const searchSchools = useCallback(async (input) => {
    try {
      const data = await request("admin/schools", { method: "GET" });
      const schools = data?.schools || [];
      const needle = (input || "").toLowerCase();
      return schools
        .filter((s) => !needle || (s.name || "").toLowerCase().includes(needle))
        .slice(0, 25)
        .map((s) => ({ value: s.id, label: s.name }));
    } catch (e) {
      return [];
    }
  }, []);

  const asyncFor = {
    user: searchUsers,
    client: searchClients,
    school: searchSchools,
  }[subjectType];

  if (asyncFor) {
    const placeholder =
      subjectType === "school"
        ? "Search schools by name…"
        : `Search ${subjectType}s…`;
    return (
      <AsyncSelect
        key={subjectType}
        cacheOptions
        defaultOptions={subjectType === "school"}
        loadOptions={asyncFor}
        value={value ? { value, label: valueLabel || value } : null}
        onChange={(option) => onChange(option ? option.value : "")}
        placeholder={placeholder}
        isClearable
        noOptionsMessage={({ inputValue }) =>
          subjectType !== "school" && inputValue.length < 2
            ? "Type 2+ characters to search"
            : "No matches"
        }
      />
    );
  }

  const fixedOptions =
    subjectType === "client_type" ? CLIENT_TYPES : EDUCATOR_TYPES;
  const selected = fixedOptions.find((o) => o.value === value) || null;
  return (
    <Select
      key={subjectType}
      options={fixedOptions}
      value={selected}
      onChange={(option) => onChange(option ? option.value : "")}
      placeholder={`Select a ${subjectType.replace("_", " ")}…`}
      isClearable
    />
  );
};
